/**
 * Platform-specific safe area utility for Android/iOS compatibility
 */

interface SafeAreaConfig {
  top: string;
  bottom: string;
  left: string;
  right: string;
}

/**
 * Detects if the current device is Android
 */
function isAndroid(): boolean {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return userAgent.includes("android");
}

/**
 * Detects if the current device is iOS
 */
function isIOS(): boolean {
  if (typeof window === "undefined") return false;

  const userAgent = window.navigator.userAgent.toLowerCase();
  return (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ipod")
  );
}

/**
 * Gets platform-specific safe area configuration
 */
export function getPlatformSafeArea(): SafeAreaConfig {
  if (isAndroid()) {
    // Android devices need larger top offset as they don't properly report safe-area-inset-top
    // and native Telegram buttons need more space
    return {
      top: `calc(80px + env(safe-area-inset-top, 0px))`,
      bottom: `max(env(safe-area-inset-bottom, 0px), 16px)`,
      left: `env(safe-area-inset-left, 0px)`,
      right: `env(safe-area-inset-right, 0px)`,
    };
  } else if (isIOS()) {
    // iOS properly reports safe-area-inset values, so we use them with a smaller fallback
    return {
      top: `calc(60px + env(safe-area-inset-top, 0px))`,
      bottom: `env(safe-area-inset-bottom, 0px)`,
      left: `env(safe-area-inset-left, 0px)`,
      right: `env(safe-area-inset-right, 0px)`,
    };
  } else {
    // Desktop or unknown platform - use minimal safe area
    return {
      top: `calc(60px + env(safe-area-inset-top, 0px))`,
      bottom: `env(safe-area-inset-bottom, 0px)`,
      left: `env(safe-area-inset-left, 0px)`,
      right: `env(safe-area-inset-right, 0px)`,
    };
  }
}

/**
 * Applies platform-specific safe area CSS variables to the document
 */
export function applyPlatformSafeArea(): void {
  if (typeof document === "undefined") return;

  const safeArea = getPlatformSafeArea();
  const root = document.documentElement;

  // Update CSS custom properties
  root.style.setProperty("--safe-area-top", safeArea.top);
  root.style.setProperty("--safe-area-bottom", safeArea.bottom);
  root.style.setProperty("--safe-area-left", safeArea.left);
  root.style.setProperty("--safe-area-right", safeArea.right);

  // Also update Telegram-specific variables
  root.style.setProperty("--tg-content-safe-area-inset-top", safeArea.top);
  root.style.setProperty(
    "--tg-content-safe-area-inset-bottom",
    safeArea.bottom
  );
  root.style.setProperty("--tg-content-safe-area-inset-left", safeArea.left);
  root.style.setProperty("--tg-content-safe-area-inset-right", safeArea.right);
}
